import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Icon,
  useColorModeValue,
  SimpleGrid,
  Badge,
  Button,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import {
  FiPercent,
  FiDollarSign,
  FiTrendingUp,
  FiGift,
  FiTag,
  FiStar,
  FiZap,
  FiSettings,
  FiChevronUp,
  FiChevronDown,
} from 'react-icons/fi';

const PricingRules = ({ value, onChange }) => {
  const [discountType, setDiscountType] = useState(value?.type || 'percentage');
  const [discountValue, setDiscountValue] = useState(value?.value || 0);
  const [mode, setMode] = useState('quick'); // 'quick' or 'custom'

  // Get currency info from qbndAdmin with fallback
  const currencyInfo = window.qbndAdmin?.currency || { symbol: '$', position: 'left' };
  const { symbol, position } = currencyInfo;

  // Determine if we should show the currency symbol before or after the input
  const showSymbolBefore = position === 'left' || position === 'left_space';
  const showSymbolAfter = position === 'right' || position === 'right_space';
  const addSpace = position === 'left_space' || position === 'right_space';

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const accentColor = 'blue.500';

  const handleChange = (type, value) => {
    const newType = type || discountType;
    const newValue = value ?? discountValue;
    setDiscountType(newType);
    setDiscountValue(newValue);
    onChange?.({
      type: newType,
      value: newValue
    });
  };

  const presetDiscounts = [
    {
      label: 'Small Discount',
      value: 10,
      description: 'Perfect for new bundles',
      icon: FiTag,
      color: 'blue'
    },
    {
      label: 'Medium Discount',
      value: 25,
      description: 'Most popular choice',
      icon: FiGift,
      color: 'purple'
    },
    {
      label: 'Large Discount',
      value: 40,
      description: 'For special occasions',
      icon: FiStar,
      color: 'orange'
    },
    {
      label: 'Mega Sale',
      value: 50,
      description: 'Maximum impact',
      icon: FiTrendingUp,
      color: 'red'
    }
  ];

  return (
    <Box>
      {/* Mode Toggle */}
      <Box mb={6}>
        <SimpleGrid columns={2} spacing={4}>
          <Button
            size="lg"
            variant={mode === 'quick' ? 'solid' : 'outline'}
            colorScheme="blue"
            onClick={() => setMode('quick')}
            leftIcon={<FiZap />}
            _hover={{
              transform: 'translateY(-2px)',
              shadow: 'md'
            }}
          >
            Quick Presets
          </Button>
          <Button
            size="lg"
            variant={mode === 'custom' ? 'solid' : 'outline'}
            colorScheme="purple"
            onClick={() => setMode('custom')}
            leftIcon={<FiSettings />}
            _hover={{
              transform: 'translateY(-2px)',
              shadow: 'md'
            }}
          >
            Custom Mode
          </Button>
        </SimpleGrid>
      </Box>

      {mode === 'quick' ? (
        // Quick Presets Mode
        <Box>
          <Box
            bg={bgColor}
            p={6}
            borderRadius="xl"
            boxShadow="xl"
            border="1px"
            borderColor={borderColor}
          >
            <VStack spacing={4} align="stretch">
              <HStack spacing={3}>
                <Icon as={FiGift} color={accentColor} />
                <Text fontSize="lg" fontWeight="medium">Quick Presets</Text>
                <Badge colorScheme="blue" variant="subtle">Recommended</Badge>
              </HStack>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                {presetDiscounts.map((preset) => (
                  <Box
                    key={preset.value}
                    p={4}
                    bg={useColorModeValue(`${preset.color}.50`, `${preset.color}.900`)}
                    borderRadius="lg"
                    cursor="pointer"
                    onClick={() => handleChange('percentage', preset.value)}
                    border="2px"
                    borderColor={discountValue === preset.value ? `${preset.color}.500` : 'transparent'}
                    transition="all 0.2s"
                    _hover={{
                      transform: 'translateY(-2px)',
                      shadow: 'md',
                      borderColor: `${preset.color}.500`
                    }}
                  >
                    <VStack spacing={2} align="start">
                      <HStack spacing={3} width="full" justify="space-between">
                        <Icon
                          as={preset.icon}
                          color={`${preset.color}.500`}
                          boxSize={5}
                        />
                        <Badge
                          colorScheme={preset.color}
                          variant={discountValue === preset.value ? 'solid' : 'subtle'}
                        >
                          {preset.value}% OFF
                        </Badge>
                      </HStack>
                      <Text fontWeight="medium" color={`${preset.color}.700`}>
                        {preset.label}
                      </Text>
                      <Text fontSize="sm" color={`${preset.color}.600`}>
                        {preset.description}
                      </Text>
                    </VStack>
                  </Box>
                ))}
              </SimpleGrid>
            </VStack>
          </Box>
        </Box>
      ) : (
        // Custom Mode
        <Box
          bg={bgColor}
          p={6}
          borderRadius="xl"
          boxShadow="xl"
          border="1px"
          borderColor={borderColor}
        >
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {/* Discount Type Selection */}
            <Box>
              <VStack align="stretch" spacing={3}>
                <Text fontWeight="medium" fontSize="sm">Discount Type</Text>
                <SimpleGrid columns={2} spacing={3}>
                  <Box
                    p={3}
                    bg={useColorModeValue(
                      discountType === 'percentage' ? 'purple.50' : 'white',
                      discountType === 'percentage' ? 'purple.900' : 'gray.800'
                    )}
                    borderRadius="lg"
                    cursor="pointer"
                    onClick={() => handleChange('percentage')}
                    border="2px"
                    borderColor={discountType === 'percentage' ? 'purple.500' : borderColor}
                    transition="all 0.2s"
                    _hover={{
                      transform: 'translateY(-2px)',
                      shadow: 'md'
                    }}
                  >
                    <VStack spacing={1}>
                      <Text
                        fontSize="xl"
                        fontWeight="bold"
                        color={discountType === 'percentage' ? 'purple.500' : 'gray.500'}
                      >
                        %
                      </Text>
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        color={discountType === 'percentage' ? 'purple.700' : 'gray.600'}
                      >
                        Percentage
                      </Text>
                    </VStack>
                  </Box>

                  <Box
                    p={3}
                    bg={useColorModeValue(
                      discountType === 'fixed' ? 'blue.50' : 'white',
                      discountType === 'fixed' ? 'blue.900' : 'gray.800'
                    )}
                    borderRadius="lg"
                    cursor="pointer"
                    onClick={() => handleChange('fixed')}
                    border="2px"
                    borderColor={discountType === 'fixed' ? 'blue.500' : borderColor}
                    transition="all 0.2s"
                    _hover={{
                      transform: 'translateY(-2px)',
                      shadow: 'md'
                    }}
                  >
                    <VStack spacing={1}>
                      <Text
                        fontSize="xl"
                        fontWeight="bold"
                        color={discountType === 'fixed' ? 'blue.500' : 'gray.500'}
                      >
                        {symbol}
                      </Text>
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        color={discountType === 'fixed' ? 'blue.700' : 'gray.600'}
                      >
                        Fixed Amount
                      </Text>
                    </VStack>
                  </Box>
                </SimpleGrid>
              </VStack>
            </Box>

            {/* Discount Value Selection */}
            <Box>
              <VStack align="stretch" spacing={3}>
                <Text fontWeight="medium" fontSize="sm">Discount Value</Text>
                <Box
                  p={4}
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius="lg"
                >
                  <InputGroup size="lg">
                    {showSymbolBefore && discountType === 'fixed' && (
                      <InputRightElement width={8} left={2}>
                        <Text>{symbol}{addSpace ? ' ' : ''}</Text>
                      </InputRightElement>
                    )}
                    <NumberInput
                      value={discountValue}
                      onChange={(value) => handleChange(undefined, parseFloat(value))}
                      min={0}
                      max={discountType === 'percentage' ? 100 : 999999}
                      size="lg"
                      keepWithinRange={true}
                      clampValueOnBlur={true}
                      width="full"
                    >
                      <NumberInputField
                        fontSize="xl"
                        fontWeight="bold"
                        textAlign="center"
                        pl={showSymbolBefore && discountType === 'fixed' ? 12 : 4}
                        pr={12}
                      />
                      <NumberInputStepper>
                        <NumberIncrementStepper
                          bg={useColorModeValue('gray.100', 'whiteAlpha.100')}
                          _hover={{
                            bg: useColorModeValue('gray.200', 'whiteAlpha.200')
                          }}
                          children={<Icon as={FiChevronUp} />}
                        />
                        <NumberDecrementStepper
                          bg={useColorModeValue('gray.100', 'whiteAlpha.100')}
                          _hover={{
                            bg: useColorModeValue('gray.200', 'whiteAlpha.200')
                          }}
                          children={<Icon as={FiChevronDown} />}
                        />
                      </NumberInputStepper>
                    </NumberInput>
                    <InputRightElement width={8} mr={8}>
                      {discountType === 'percentage' ? (
                        <FiPercent />
                      ) : (
                        showSymbolAfter && <Text>{addSpace ? ' ' : ''}{symbol}</Text>
                      )}
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </VStack>
            </Box>
          </SimpleGrid>
        </Box>
      )}
    </Box>
  );
};

export default PricingRules;